import * as SocketIOClient from 'socket.io-client'
import assets from '../assets'
import { API_URL } from '../config'
import { Order } from '../redux/reducers/order.reducer'
import { store } from '../redux/store'
import { Booking } from '../redux/reducers/booking.reducer'
import { Reservation } from '../redux/reducers/reservation.reducer'
import { ProductRecentLiker } from '../types/productLike'

export type ProductLikeType = 'food' | 'event' | 'appointment'

export interface ProductLikeUpdatedPayload {
    shopId: string
    productId: string
    productType: ProductLikeType
    likeCount: number
    recentLikers: ProductRecentLiker[]
}

export interface ShopOrdersPauseUpdatedPayload {
    shopId: string
    ordersPausedUntil: string | null
}

type ConnectCallback = (socket: Socket) => void
type OnCallback = (order: Order) => void
type OnCallbackBooking = (booking: Booking) => void
type OnCallbackReservation = (reservation: Reservation) => void
type OnProductLikeUpdatedCallback = (payload: ProductLikeUpdatedPayload) => void
type OnShopOrdersPauseUpdatedCallback = (payload: ShopOrdersPauseUpdatedPayload) => void


export default class Socket {
    private static instance: Socket | null = null
    private socket: SocketIOClient.Socket | null = null
    private shopViewerSocket: SocketIOClient.Socket | null = null
    private shopViewerShopId: string | null = null

    private isConnectionRequestSend: boolean = false

    /** Reused after `primeNotificationAudio()` so socket-driven `play()` is allowed (autoplay policy). */
    private bellAudio: HTMLAudioElement | null = null
    private softwareAudio: HTMLAudioElement | null = null
    private notificationAudioPrimed = false
    private lastBellPlayedAt = 0

    private constructor() { }

    public static init() {
        if (Socket.instance === null) {
            Socket.instance = new Socket()
        }

        return Socket.instance
    }

    public connect(callback?: ConnectCallback) {
        if (!this.socket?.connected && !this.isConnectionRequestSend) {
            this.socket = SocketIOClient.io(API_URL, {
                query: {
                    userId: store.getState().authAdmin?.id,
                    role: store.getState().authAdmin?.role,
                    shopId: store.getState().authAdmin?.shop.id
                }
            })

            this.isConnectionRequestSend = true

            this.socket.on('connect', () => {
                if (this.socket?.connected) {
                    if (Socket.instance) {
                        callback?.(Socket.instance)
                    }
                }
            })
        }
    }

    public connectShop(shopId: string, userId?: string) {
        if (this.shopViewerSocket?.connected && this.shopViewerShopId === shopId) {
            return
        }

        this.disconnectShop()

        this.shopViewerSocket = SocketIOClient.io(API_URL, {
            query: {
                shopId,
                userId: userId || 'guest',
                role: 'user'
            }
        })
        this.shopViewerShopId = shopId
    }

    public disconnectShop() {
        if (this.shopViewerSocket) {
            this.shopViewerSocket.off('productLikeUpdated')
            this.shopViewerSocket.off('shopOrdersPauseUpdated')
            this.shopViewerSocket.disconnect()
            this.shopViewerSocket = null
            this.shopViewerShopId = null
        }
    }

    public onProductLikeUpdated(callback?: OnProductLikeUpdatedCallback) {
        this.shopViewerSocket?.off('productLikeUpdated')
        this.shopViewerSocket?.on('productLikeUpdated', (payload: ProductLikeUpdatedPayload) => {
            callback?.(payload)
        })
    }

    public offProductLikeUpdated() {
        this.shopViewerSocket?.off('productLikeUpdated')
    }

    public onShopOrdersPauseUpdated(callback?: OnShopOrdersPauseUpdatedCallback) {
        this.shopViewerSocket?.off('shopOrdersPauseUpdated')
        this.shopViewerSocket?.on('shopOrdersPauseUpdated', (payload: ShopOrdersPauseUpdatedPayload) => {
            callback?.(payload)
        })
    }

    public offShopOrdersPauseUpdated() {
        this.shopViewerSocket?.off('shopOrdersPauseUpdated')
    }

    public onAdminShopOrdersPauseUpdated(callback?: OnShopOrdersPauseUpdatedCallback) {
        this.socket?.off('shopOrdersPauseUpdated')
        this.socket?.on('shopOrdersPauseUpdated', (payload: ShopOrdersPauseUpdatedPayload) => {
            callback?.(payload)
        })
    }

    public offAdminShopOrdersPauseUpdated() {
        this.socket?.off('shopOrdersPauseUpdated')
    }

    public getSocket() {
        return this.socket
    }

    public onNewOrder(callback?: OnCallback) {

        this.socket?.on('newOrder', async (order: Order) => {

            callback?.(order);
            if (store.getState().authAdmin?.role !== 'chef') {
                await this.playSound()
            }
        });
    }

    public onOrderPaymentVerified(callback?: OnCallback) {
        this.socket?.on('orderPaymentVerified', async (order: Order) => {
            callback?.(order)
        })
    }
    public onNewBooking(callback?: OnCallbackBooking) {

        this.socket?.on('newBooking', async (booking: Booking) => {

            callback?.(booking);
            await this.playSound();
        });
    }
    public onNewReservation(callback?: OnCallbackReservation) {

        this.socket?.on('newReservation', async (reservation: Reservation) => {

            callback?.(reservation);
            await this.playSound();
        });
    }

    public onOrderReady(callback?: OnCallback) {
        this.socket?.off('orderReady')
        this.socket?.on('orderReady', async (order: Order) => {
            callback?.(order)
            await this.playSound2();
        })
    }

    /**
     * Browsers block `Audio.play()` without a prior user gesture. Call this synchronously from
     * pointer/key handlers after admin login so `newOrder` / `newBooking` sounds can play later.
     */
    public primeNotificationAudio(): void {
        if (this.notificationAudioPrimed) {
            return
        }
        try {
            const bell = new Audio(assets.audios.bell)
            bell.preload = 'auto'
            bell.setAttribute('playsinline', 'true')
            const software = new Audio(assets.audios.software)
            software.preload = 'auto'
            software.setAttribute('playsinline', 'true')

            const markPrimed = () => {
                this.notificationAudioPrimed = true
                this.bellAudio = bell
                this.softwareAudio = software
            }

            void bell.play().then(() => {
                bell.pause()
                bell.currentTime = 0
                markPrimed()
            }).catch(() => {
                markPrimed()
            })
        } catch {
            this.notificationAudioPrimed = true
        }
    }

    /** Play new-order bell (e.g. Chef Dashboard). Debounced to avoid double ring with socket handler. */
    public playBellSound(options?: { loop?: boolean }) {
        return this.playSound(options?.loop ?? false)
    }

    public stopBellSound() {
        const audio = this.bellAudio
        if (audio) {
            audio.pause()
            audio.currentTime = 0
            audio.loop = false
        }
    }

    private async playSound(loop = false) {
        const now = Date.now()
        if (!loop && now - this.lastBellPlayedAt < 400) {
            return
        }
        if (!loop) {
            this.lastBellPlayedAt = now
        }

        try {
            const audio = this.bellAudio ?? new Audio(assets.audios.bell)
            if (!this.bellAudio) {
                this.bellAudio = audio
            }
            audio.loop = loop
            audio.currentTime = 0
            await audio.play()
        } catch (error) {
            console.warn('Order / booking notification sound:', error)
        }
    }
    private async playSound2() {
        try {
            const audio = this.softwareAudio ?? new Audio(assets.audios.software)
            audio.currentTime = 0
            await audio.play()
        } catch (error) {
            console.warn('Order-ready notification sound:', error)
        }
    }

    public offNewOrder() {

        this.socket?.off('newOrder')
    }
    public offOrderPaymentVerified() {
        this.socket?.off('orderPaymentVerified')
    }
    public offOrderReady() {
        this.socket?.off('orderReady')
    }

    public close() {
        if (this.socket) {
            if (this.socket.connected) {
                this.socket.off('connect')
                this.socket.disconnect()
            }
            this.socket = null
        }

        this.stopBellSound()
        this.bellAudio = null
        this.softwareAudio = null
        this.notificationAudioPrimed = false

        if (Socket.instance) {
            Socket.instance = null
        }
    }

   public onOrderProcessing(callback?: OnCallback) {
    this.socket?.off('orderProcessing')

    this.socket?.on('orderProcessing', (order: Order) => {
        callback?.(order)
    })
    }
    
    public offOrderProcessing() {
    this.socket?.off('orderProcessing')
    }
    
}